import React from 'react';
import PropTypes from 'prop-types';

/**
 * Prev / next paging, and how many rows a page holds.
 *
 * `pages` when the endpoint returns a count, so the footer can say where the end
 * is; callers that only get rows back leave it out and pass `hasNext`
 * themselves, having guessed from a full page.
 *
 * The size control lives here because it is paging, and it used to live in the
 * filter card as a text field labelled "Results per page" — sitting between
 * Status and Facility, where it read as another way to narrow the list rather
 * than as a way to see more of it. It also took any number somebody typed,
 * including 5000, which is a request nobody meant to make. Four sizes, next to
 * the control it actually belongs to.
 *
 * Callers that cannot change the size — the endpoints that ignore it — simply do
 * not pass `onPerPageChange`, and no control is drawn.
 */

const SIZES = [15, 30, 50, 100];

export default function Pager({ page, pages, hasNext, perPage, onPerPageChange, onChange }) {
  return (
    <div className="next-pager">
      <span className="next-muted">
        Page {page}{pages ? ` of ${pages.toLocaleString()}` : ''}
      </span>

      {onPerPageChange && (
        <label className="next-pager__size">
          <span className="next-muted">Rows</span>
          <select
            className="next-input next-input--sm"
            value={perPage}
            onChange={event => onPerPageChange(Number(event.target.value))}
          >
            {/* A size arriving from a hand-edited URL is still the size in use,
                so it is offered rather than silently swapped for the default. */}
            {(SIZES.includes(perPage) ? SIZES : [...SIZES, perPage].sort((a, b) => a - b))
              .map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
      )}

      <div className="next-pager__spacer" />
      <button
        type="button"
        className="next-btn next-btn--sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        ‹ Prev
      </button>
      <button
        type="button"
        className="next-btn next-btn--sm"
        disabled={!hasNext}
        onClick={() => onChange(page + 1)}
      >
        Next ›
      </button>
    </div>
  );
}

Pager.propTypes = {
  page: PropTypes.number.isRequired,
  pages: PropTypes.number,
  hasNext: PropTypes.bool,
  perPage: PropTypes.number,
  // Omitted where the endpoint ignores the size; the control is then not drawn.
  onPerPageChange: PropTypes.func,
  onChange: PropTypes.func.isRequired
};
